import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useHealthProviderPostalCodeItems } from '../../../../data/data/health-providers/use-health-provider-postal-code-items';
import { useLanguageValues } from '../../../../data/data/select-values';
import {
  CreatePatientItemDocument,
  Gender,
  GetPatientGroupItemsDocument,
  GetPatientItemsDocument,
  Language,
  MfaAuthenticationType,
  PatientItemDataFragment,
} from '../../../../data/types/api/graphql';
import { formatDate } from '../../../../data/utils/date.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { InputSelectController } from '../../../../elements/form/controllers/input-select';
import { FormRow } from '../../../../elements/form/form';
import { BaseInputIcon } from '../../../../elements/form/input/base-input';
import { InputCheckboxType } from '../../../../elements/form/input/input-checkbox';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Message, MessageType } from '../../../../elements/message/message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { usePatientForm } from '../../forms/patient-form/use-patient-form';

type PatientCreateModalProps = {
  healthProviderId: string | null | undefined;
  onSaved?: (patient: PatientItemDataFragment) => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function PatientCreateModal({
  healthProviderId,
  onSaved,
  onCancel,
  onClose,
}: PatientCreateModalProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const { form, formIsValid, setErrors } = usePatientForm();

  const [invalidPostalCode, setInvalidPostalCode] = useState(false);

  const { data: healthProviderPostalCodeItemsData } =
    useHealthProviderPostalCodeItems({
      healthProviderId,
    });

  const checkPostalCode = useCallback(
    (value: string) => {
      setInvalidPostalCode(
        !healthProviderPostalCodeItemsData.some(c => value.startsWith(c))
      );
    },
    [healthProviderPostalCodeItemsData]
  );
  const { data: languageData } = useLanguageValues();

  const [createPatientItem, { loading: createPatientItemLoading }] =
    useMutation(CreatePatientItemDocument, {
      refetchQueries: [GetPatientGroupItemsDocument, GetPatientItemsDocument],
    });

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();
      try {
        const result = await createPatientItem({
          variables: {
            ...values,
            healthProviderId: healthProviderId ?? '',
            firstName: values.firstName ?? '',
            lastName: values.lastName ?? '',
            email: values.email ?? '',
            phoneNumber: values.phoneNumber
              ? values.phoneNumber.replace(/\s/g, '')
              : '',
            postalCode: values.postalCode ?? '',
            app: values.app === true,
            dutchInsurance: values.dutchInsurance === true,
            noShowProtocol: values.noShowProtocol === true,
          },
        });

        if (result.data?.patientCreate.patient) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'patient.patient-create.success',
            }),
          });

          onSaved?.(result.data.patientCreate.patient);

          onClose?.();
        } else {
          setErrors(result.data?.patientCreate?.errors);
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    intl,
    form,
    healthProviderId,
    createPatientItem,
    showAlertMessage,
    setErrors,
    onSaved,
    onClose,
  ]);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  const appUser = form.watch('app') === true;

  return (
    <>
      <Modal
        testId="patient-create"
        title={<CustomFormattedMessage id="patient.patient-create.title" />}
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          <>
            <Button
              type={ButtonType.SECONDARY}
              inverted
              onClick={() => onHandleCancel(false)}
            >
              <CustomFormattedMessage id="common.cancel" />
            </Button>

            <Button
              icon={
                createPatientItemLoading ? ButtonIcon.LOADING : ButtonIcon.NONE
              }
              disabled={!formIsValid || createPatientItemLoading}
              onClick={onHandleSubmit}
            >
              <CustomFormattedMessage id="patient.patient-create.save" />
            </Button>
          </>
        }
      >
        <FormProvider {...form}>
          <FormRow>
            <InputFieldController
              name="firstName"
              label={
                <>
                  <CustomFormattedMessage id="common.firstname" />
                  <small>*</small>
                </>
              }
              placeholder={intl.formatMessage({ id: 'common.firstname' })}
            />
          </FormRow>

          <FormRow>
            <InputFieldController
              name="lastName"
              label={
                <>
                  <CustomFormattedMessage id="common.lastname" />
                  <small>*</small>
                </>
              }
              placeholder={intl.formatMessage({ id: 'common.lastname' })}
            />
          </FormRow>

          <FormRow>
            <InputFieldController
              icon={BaseInputIcon.DATE}
              type="date"
              name="birthDate"
              label={
                <>
                  <CustomFormattedMessage id="common.birthdate" />
                  <small>*</small>
                </>
              }
              max={formatDate(new Date())}
            />
          </FormRow>

          <FormRow>
            <InputCheckboxController<Gender>
              type={InputCheckboxType.RADIO}
              name="gender"
              label={
                <>
                  <CustomFormattedMessage id="common.gender" /> <small>*</small>
                </>
              }
              options={[
                {
                  key: Gender.Male,
                  title: <CustomFormattedMessage id="common.gender.male" />,
                },
                {
                  key: Gender.Female,
                  title: <CustomFormattedMessage id="common.gender.female" />,
                },
                {
                  key: Gender.Other,
                  title: <CustomFormattedMessage id="common.gender.other" />,
                },
              ]}
            />
          </FormRow>

          <FormRow>
            <InputCheckboxController<boolean>
              name="app"
              label={<CustomFormattedMessage id="patient.app-user" />}
              options={[
                {
                  key: true,
                  title: (
                    <CustomFormattedMessage id="patient.app-user-placeholder" />
                  ),
                },
              ]}
            />
          </FormRow>

          <FormRow>
            <InputSelectController<Language>
              name="language"
              label={
                <>
                  <CustomFormattedMessage id="common.language" />
                  <small>*</small>
                </>
              }
              options={languageData}
            />
          </FormRow>
          <FormRow>
            <InputCheckboxController<MfaAuthenticationType>
              type={InputCheckboxType.RADIO}
              name="mfaAuthenticationType"
              label={
                <>
                  <CustomFormattedMessage id="common.mfa-authentication-type" />{' '}
                  <small>*</small>
                </>
              }
              options={[
                {
                  key: MfaAuthenticationType.Sms,
                  title: (
                    <CustomFormattedMessage id="common.mfa-authentication-type.sms" />
                  ),
                },
                {
                  key: MfaAuthenticationType.Email,
                  title: (
                    <CustomFormattedMessage id="common.mfa-authentication-type.email" />
                  ),
                },
              ]}
            />
          </FormRow>
          <FormRow>
            <InputFieldController
              type="phone"
              name="phoneNumber"
              label={
                <>
                  <CustomFormattedMessage id="common.phone" />
                  {appUser && <small>*</small>}
                </>
              }
              placeholder={intl.formatMessage({
                id: 'common.phone',
              })}
            />
          </FormRow>

          <FormRow>
            <InputFieldController
              name="email"
              label={
                <>
                  <CustomFormattedMessage id="common.email" />
                  {appUser && <small>*</small>}
                </>
              }
              placeholder={intl.formatMessage({ id: 'common.email' })}
            />
          </FormRow>

          <FormRow>
            <InputFieldController
              name="postalCode"
              label={
                <>
                  <CustomFormattedMessage id="common.postalcode" />
                  <small>*</small>
                </>
              }
              placeholder={intl.formatMessage({
                id: 'common.postalcode',
              })}
              onChange={() => {
                setInvalidPostalCode(false);
              }}
              onBlur={checkPostalCode}
            />
            {invalidPostalCode && (
              <Message
                type={MessageType.WARNING}
                message={
                  <CustomFormattedMessage id="patient.postalcode.warning" />
                }
              />
            )}
          </FormRow>

          <FormRow>
            <InputCheckboxController<boolean>
              name="dutchInsurance"
              label={<CustomFormattedMessage id="patient.insurance" />}
              options={[
                {
                  key: true,
                  title: (
                    <CustomFormattedMessage id="patient.insurance-placeholder" />
                  ),
                },
              ]}
            />
          </FormRow>

          <FormRow>
            <InputCheckboxController<boolean>
              name="noShowProtocol"
              label={<CustomFormattedMessage id="patient.no-show-protocol" />}
              options={[
                {
                  key: true,
                  title: (
                    <CustomFormattedMessage id="patient.no-show-protocol-placeholder" />
                  ),
                },
              ]}
            />
          </FormRow>
        </FormProvider>
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
